import { useState } from "react";
import { Check, ErrorOutline } from "@mui/icons-material";
import {
    useGetDeviceSoftwareVersions,
    useGetAvailableSoftwareVersions,
    useSetDeviceSoftwareVersion,
} from "../../../api/routes/devices";

const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(date);
};

const SoftwareVersions = ({ deviceId }: { deviceId: string }) => {
    const { data: deviceVersions, isLoading: loadingCurrent } = useGetDeviceSoftwareVersions(deviceId);
    const { data: availableVersions, isLoading: loadingAvailable } = useGetAvailableSoftwareVersions(deviceId);
    const setVersion = useSetDeviceSoftwareVersion();
    const [confirmVersionId, setConfirmVersionId] = useState<number | null>(null);

    const activeVersion = deviceVersions?.find((v: any) => v.is_active);
    const activeVersionId = activeVersion?.softwareVersion?.id;

    const handleSetVersion = (versionId: number) => {
        if (versionId === activeVersionId) return;
        setConfirmVersionId(versionId);
    };

    const confirmSet = () => {
        if (confirmVersionId === null) return;
        setVersion.mutate(
            { deviceId, versionId: confirmVersionId },
            { onSettled: () => setConfirmVersionId(null) }
        );
    };

    const isLoading = loadingCurrent || loadingAvailable;

    return (
        <div className="rounded-lg border bg-card mb-6">
            <div className="px-6 py-4 border-b">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Software Version
                </h3>
            </div>
            <div className="p-6">
                {isLoading ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                ) : !availableVersions || availableVersions.length === 0 ? (
                    <div className="rounded-md border border-dashed p-8 text-center">
                        <ErrorOutline className="text-muted-foreground mx-auto mb-2" style={{ fontSize: 32 }} />
                        <h4 className="text-sm font-medium text-foreground mb-1">No Software Versions</h4>
                        <p className="text-xs text-muted-foreground">
                            No software versions are available for this device model.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Current version */}
                        {activeVersion && (
                            <div className="rounded-md border border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-700">
                                        Active
                                    </span>
                                    <span className="text-sm font-medium text-foreground">
                                        {activeVersion.softwareVersion?.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    {activeVersion.softwareVersion?.file && (
                                        <span className="font-mono">{activeVersion.softwareVersion.file}</span>
                                    )}
                                    {activeVersion.softwareVersion?.releaseDate && (
                                        <span>Released {formatDate(activeVersion.softwareVersion.releaseDate)}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Available versions */}
                        <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                                Available Versions
                            </p>
                            <div className="space-y-1 max-h-64 overflow-y-auto">
                                {availableVersions.map((version: any) => {
                                    const isActive = version.id === activeVersionId;
                                    const isConfirming = confirmVersionId === version.id;

                                    return (
                                        <div
                                            key={version.id}
                                            className={`flex items-center justify-between rounded-md border px-4 py-3 transition-colors ${
                                                isActive
                                                    ? "border-green-200 bg-green-50/30 dark:bg-green-950/10 dark:border-green-800"
                                                    : "hover:bg-muted/50 cursor-pointer"
                                            }`}
                                            onClick={() => !isActive && !setVersion.isPending && handleSetVersion(version.id)}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                    isActive
                                                        ? "border-green-500 bg-green-500"
                                                        : "border-muted-foreground/30"
                                                }`}>
                                                    {isActive && <Check style={{ fontSize: 14, color: "white" }} />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-foreground truncate">
                                                        {version.name}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        {version.file && (
                                                            <span className="font-mono truncate">{version.file}</span>
                                                        )}
                                                        {version.releaseDate && (
                                                            <span className="flex-shrink-0">{formatDate(version.releaseDate)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {isConfirming && (
                                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                    <span className="text-xs text-muted-foreground">Set as active?</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); confirmSet(); }}
                                                        disabled={setVersion.isPending}
                                                        className="text-xs font-medium px-2.5 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                                    >
                                                        {setVersion.isPending ? "Saving..." : "Confirm"}
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setConfirmVersionId(null); }}
                                                        className="text-xs font-medium px-2.5 py-1 rounded-md border hover:bg-muted"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}

                                            {isActive && !isConfirming && (
                                                <span className="text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0 ml-3">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SoftwareVersions;